import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import * as muiLocales from '@mui/material/locale';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as muiXLocales from '@mui/x-date-pickers/locales';
import { AppGlobalStyles } from "app/appGlobalStyles";
import * as mprcLocales from 'components/locales';
import { MprcThemeProvider } from "components/MprcThemeProvider";
import { useSession } from "providers/SessionProvider";
import React, { PropsWithChildren, useMemo } from "react";
import { AppThemes } from "themes/appThemes";

type MuiSupportedLocales = keyof typeof muiLocales;
type MuiXSupportedLocales = keyof typeof muiXLocales;
type MprcSupportedLocales = keyof typeof mprcLocales;

/**
 * Cache de Styles do Material-UI
 * */
const muiCache = createCache({
    "key": "mui",
    "prepend": true
});

/**
 * Theme Provider
 * @param param0
 */
export const AppThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const session = useSession();
    const culture = session.uiCulture.replace('-', '') as MuiSupportedLocales;

    const selectedTheme = useMemo(() => {
        return AppThemes.find(p => p.key == session.config.tenantUIConfig.themeName) ?? AppThemes.find(p => p.key == "default");
    }, [session.config.tenantUIConfig.themeName]);

    const themeWithLocale = useMemo(
        () => createTheme(
            {
                ...selectedTheme?.theme
            },
            muiLocales[culture],
            muiXLocales[culture as MuiXSupportedLocales]
        ),
        [culture, session.config.tenantUIConfig.themeName]
    );

    const mprcWithLocale = useMemo(
        () => mprcLocales[culture as MprcSupportedLocales],
        [culture, session.config.tenantUIConfig.themeName]
    );

    return (
        <CacheProvider value={muiCache}>
            <ThemeProvider theme={themeWithLocale}>
                <AppGlobalStyles />
                <MprcThemeProvider theme={mprcWithLocale} >
                    {children}
                </MprcThemeProvider>
            </ThemeProvider>
        </CacheProvider>
    );
};
